import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { ThemeService } from '~shared/services/accessibility/theme.service';
import { AuthTabSyncService } from '~shared/services/auth/tab-sync/auth-tab-sync.service';
import { AuthenticationService } from '~shared/services/authentication.service';
import { CurrentUserService } from '~shared/services/current-user/current-user.service';
import { LocaleService } from '~shared/services/locale.service';
import { ModulesService } from '~shared/services/modules/modules.service';
import { mapMeModules } from '~shared/utils/me-modules.mapper';

export function initApp(
  localeService: LocaleService,
  authService: AuthenticationService,
  currentUserService: CurrentUserService,
  modulesService: ModulesService,
  authTabSync: AuthTabSyncService,
  translate: TranslateService,
  themeService: ThemeService
): () => Promise<unknown> {
  // Un onglet ouvert depuis un onglet connecté (nouvel onglet, URL collée)
  // reçoit d'abord sa session : les gardes de route ne décident qu'ensuite.
  return () =>
    authTabSync.requestSessionToken().then((adopted) =>
      startApp(
        localeService,
        authService,
        currentUserService,
        modulesService,
        themeService
      )
        // Le catalogue i18n a commencé à se charger sans jeton (construction
        // de TranslateService) : on y ajoute les traductions privées du compte.
        .then(() =>
          adopted
            ? firstValueFrom(translate.reloadLang(localeService.getLanguage()))
            : undefined
        )
    );
}

function startApp(
  localeService: LocaleService,
  authService: AuthenticationService,
  currentUserService: CurrentUserService,
  modulesService: ModulesService,
  themeService: ThemeService
): Promise<unknown> {
  // Pose le thème d'accessibilité stocké (data-theme) avant le premier rendu,
  // comme la langue plus bas. Synchrone : lecture localStorage.
  themeService.init();

  // Fire /me en parallèle des translations sans bloquer le bootstrap.
  // Avant ce changement, /me ne partait qu'après l'instanciation de
  // AppComponent (= après resolution du APP_INITIALIZER), serialisant
  // /translations/ui puis /me. Maintenant les deux courent en parallèle :
  // l'app devient interactive ~1 s plus tôt sur un cold load.
  if (authService.getJwtToken() && currentUserService.user() === null) {
    currentUserService.load().subscribe({
      next: (me) => {
        const modules = mapMeModules(me);
        modulesService.setModules(modules);
      },
      error: () => {
        // 401 géré par l'interceptor d'authentification
      },
    });
  }
  return localeService.init();
}
